/**
 * Sheet Manager
 *
 * Handles opening and closing of header sheets.
 * Also supports opening sheets programmatically via the hero search trigger.
 */

document.addEventListener('DOMContentLoaded', () => {
  // Portal pass: any sheet with [data-portal-to="<selector>"] gets
  // moved into that container before SheetManager binds. Keeps sheet
  // markup co-located with its source template (e.g. AllyRewardSingle)
  // while letting it inherit positioning from the floating header's
  // .header-dropdowns-container, so its absolute top:0 anchors right
  // under the masthead instead of leaving a 20-30px gap below.
  document.querySelectorAll('[data-portal-to]').forEach((node) => {
    const target = document.querySelector(node.getAttribute('data-portal-to'));
    if (target && !target.contains(node)) {
      target.appendChild(node);
    }
  });

  class SheetManager {
    constructor() {
      this.activeSheet = null;
      this.overlay = document.getElementById('sheetOverlay');
      this._openedThisTick = false;

      this.init();
    }

    init() {
      // Global delegated listener for elements with [data-sheet-target] (handles dynamic/PJAX elements)
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-sheet-target]');
        if (trigger) {
          const href = trigger.getAttribute('href');
          if (!href || href === '#') {
            e.preventDefault();
          }
          e.stopPropagation();

          const targetId = trigger.getAttribute('data-sheet-target');
          this.toggle(targetId, trigger);
        }
      });

      // Hero search trigger — opens search sheet
      document.querySelectorAll('.bfwt-js-search-trigger').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const searchSheet = document.getElementById('searchSheet');
          const trigger = document.querySelector(
            '[data-sheet-target="searchSheet"]'
          );
          if (searchSheet && trigger) {
            this.open(searchSheet, trigger);
          }
        });
      });

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeAll());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAll();
        }
      });

      // Close button + outside click
      document.addEventListener('click', (e) => {
        // If sheet was opened in the current event loop tick, ignore outside clicks to prevent instant closing
        if (this._openedThisTick) {
          return;
        }

        // Close button (data-sheet-close attribute or legacy class names)
        if (
          e.target.closest('[data-sheet-close]') ||
          e.target.closest('.bfwt-btn-close') ||
          e.target.closest('.bfwt-sheet-close') ||
          e.target.closest('.bfwt-sheet__close')
        ) {
          this.closeAll();
          return;
        }

        // If the clicked element is no longer in the DOM, it was likely removed/replaced
        // by a click handler inside the sheet (e.g. innerHTML updates on buttons or chips).
        // We should not treat this as an outside click.
        if (e.target instanceof Node && !document.body.contains(e.target)) {
          return;
        }

        // Outside click — but NOT inside sheets or triggers
        // Note: We check specifically for Is Active sheets to be sure
        if (
          this.activeSheet &&
          e.target &&
          typeof e.target.closest === 'function' &&
          !e.target.closest('.bfwt-floating-sheet') &&
          !e.target.closest('[data-sheet-target]') &&
          !e.target.closest('.bfwt-js-search-trigger')
        ) {
          this.closeAll();
        }
      });

      // Resize listener for active sheet height adjustment on mobile
      const handleResize = () => {
        if (this.activeSheet) {
          this.updateMobileHeight();
        }
      };
      window.addEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
      }

      // Recalculate sheet heights when items are clicked inside active sheet
      document.addEventListener('click', (e) => {
        if (this.activeSheet && e.target instanceof Node && this.activeSheet.contains(e.target)) {
          setTimeout(() => {
            this.updateMobileHeight();
          }, 150);
        }
      });

      // Initialize search sheet directory tabs and mobile navigation
      this.initSearchSheetDirectory();
      this.initReviewsSheetDirectory();
      this.initSavedSheetDirectory();
    }

    initSearchSheetDirectory() {
      const sheet = document.getElementById('searchSheet');
      const directory = document.getElementById('bfwt-search-directory');
      if (!sheet || !directory) return;

      const tabButtons = sheet.querySelectorAll('.bfwt-find-deals-tab-btn');
      const panels = sheet.querySelectorAll('.bfwt-find-deals-panel');
      const sidebar = sheet.querySelector('.bfwt-sidebar-el');
      const contentPane = sheet.querySelector('.bfwt-content-pane-el');

      window.bfwtUpdateMobileHeight = () => {
        if (window.innerWidth >= 768) {
          sheet.style.height = '';
          return;
        }

        const header = sheet.querySelector('.bfwt-search-header-row');
        const filters = sheet.querySelector('#bfwt-search-filters');
        const discoveryStrip = sheet.querySelector('#bfwt-search-discovery-strip');
        const recentBrands = sheet.querySelector('#bfwt-search-recent-brands');
        const footer = sheet.querySelector('.bfwt-search-footer-row');
        const headerHeight = header ? header.offsetHeight : 52;
        const footerHeight = footer ? footer.offsetHeight : 42;
        const filtersHeight =
          filters && !filters.classList.contains('tw-hidden')
            ? filters.offsetHeight
            : 0;
        const discoveryHeight =
          discoveryStrip && !discoveryStrip.classList.contains('tw-hidden')
            ? discoveryStrip.offsetHeight
            : 0;
        const recentBrandsHeight =
          recentBrands && !recentBrands.classList.contains('tw-hidden')
            ? recentBrands.offsetHeight
            : 0;

        const vh = window.visualViewport
          ? window.visualViewport.height
          : window.innerHeight;
        const rect = sheet.getBoundingClientRect();
        const topOffset = rect && rect.top > 0 ? rect.top : 56;
        const maxAllowedHeight = Math.max(280, vh - topOffset - 16);

        if (directory.classList.contains('tw-hidden')) {
          sheet.style.height = maxAllowedHeight + 'px';
          return;
        }

        const isSidebarActive =
          sidebar && !sidebar.classList.contains('max-md:tw--translate-x-full');
        let contentHeight = 350;

        if (isSidebarActive) {
          const sidebarInner = sidebar.querySelector('div');
          contentHeight = sidebarInner ? sidebarInner.offsetHeight : 350;
        } else {
          const activePanel = Array.from(panels).find(
            (p) => !p.classList.contains('tw-hidden')
          );
          if (activePanel) {
            contentHeight = activePanel.scrollHeight + 32;
          }
        }

        const targetHeight = Math.min(
          headerHeight + filtersHeight + discoveryHeight + recentBrandsHeight + contentHeight + footerHeight,
          maxAllowedHeight
        );

        sheet.style.height = targetHeight + 'px';
      };

      tabButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetId = btn.getAttribute('data-target-panel');

          tabButtons.forEach((b) => {
            b.classList.remove(
              'is-active',
              'tw-bg-ink/[0.06]',
              'dark:tw-bg-white/[0.08]',
              'tw-text-ink',
              'tw-border-line/80',
              'tw-bg-accent/[0.08]',
              'tw-text-accent',
              'tw-border-accent/40'
            );
            b.classList.add(
              'tw-bg-transparent',
              'tw-text-secondary',
              'tw-border-transparent'
            );
          });
          btn.classList.add(
            'is-active',
            'tw-bg-ink/[0.06]',
            'dark:tw-bg-white/[0.08]',
            'tw-text-ink',
            'tw-border-line/80'
          );
          btn.classList.remove(
            'tw-bg-transparent',
            'tw-text-secondary',
            'tw-border-transparent'
          );

          panels.forEach((p) => p.classList.add('tw-hidden'));
          const activePanel = document.getElementById(targetId);
          if (activePanel) {
            activePanel.classList.remove('tw-hidden');
          }

          if (window.innerWidth < 768 && sidebar && contentPane) {
            sidebar.classList.add('max-md:tw--translate-x-full');
            contentPane.classList.remove('max-md:tw-translate-x-full');
          }

          if (typeof window.bfwtUpdateMobileHeight === 'function') {
            window.bfwtUpdateMobileHeight();
          }
        });
      });

      sheet.querySelectorAll('.bfwt-find-deals-back-btn').forEach((backBtn) => {
        backBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (sidebar && contentPane) {
            sidebar.classList.remove('max-md:tw--translate-x-full');
            contentPane.classList.add('max-md:tw-translate-x-full');
          }

          if (typeof window.bfwtUpdateMobileHeight === 'function') {
            window.bfwtUpdateMobileHeight();
          }
        });
      });
    }

    initSavedSheetDirectory() {
      const sheet = document.getElementById('sheet-saved');
      if (!sheet) return;

      const tabButtons = sheet.querySelectorAll('.bfwt-saved-tab-btn');
      const panels = sheet.querySelectorAll('.bf-saved-panel');
      const sidebar = sheet.querySelector('.bfwt-saved-sidebar-el');
      const contentPane = sheet.querySelector('.bfwt-saved-content-pane-el');

      tabButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetId = btn.getAttribute('data-target-panel');

          tabButtons.forEach((b) => {
            b.classList.remove(
              'is-active',
              'tw-bg-ink/[0.06]',
              'dark:tw-bg-white/[0.08]',
              'tw-text-ink',
              'tw-border-line/80',
              'tw-bg-accent/[0.08]',
              'tw-text-accent',
              'tw-border-accent/40'
            );
            b.classList.add(
              'tw-bg-transparent',
              'tw-text-secondary',
              'tw-border-transparent'
            );
          });
          btn.classList.add(
            'is-active',
            'tw-bg-ink/[0.06]',
            'dark:tw-bg-white/[0.08]',
            'tw-text-ink',
            'tw-border-line/80'
          );
          btn.classList.remove(
            'tw-bg-transparent',
            'tw-text-secondary',
            'tw-border-transparent'
          );

          panels.forEach((p) => p.classList.add('tw-hidden'));
          const activePanel = document.getElementById(targetId);
          if (activePanel) {
            activePanel.classList.remove('tw-hidden');
          }

          if (window.innerWidth < 768 && sidebar && contentPane) {
            sidebar.classList.add('max-md:tw--translate-x-full');
            contentPane.classList.remove('max-md:tw-translate-x-full');
          }

          this.updateMobileHeight();
        });
      });

      sheet.querySelectorAll('.bfwt-saved-back-btn').forEach((backBtn) => {
        backBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (sidebar && contentPane) {
            sidebar.classList.remove('max-md:tw--translate-x-full');
            contentPane.classList.add('max-md:tw-translate-x-full');
          }
          this.updateMobileHeight();
        });
      });
    }

    initReviewsSheetDirectory() {
      const sheet = document.getElementById('sheet-reviews');
      const directory = document.getElementById('bfwt-reviews-directory');
      if (!sheet || !directory) return;

      const tabButtons = sheet.querySelectorAll('.bfwt-reviews-tab-btn');
      const panels = sheet.querySelectorAll('.bfwt-reviews-panel');
      const sidebar = sheet.querySelector('.bfwt-reviews-sidebar-el');
      const contentPane = sheet.querySelector('.bfwt-reviews-content-pane-el');

      tabButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetId = btn.getAttribute('data-target-panel');

          tabButtons.forEach((b) => {
            b.classList.remove(
              'is-active',
              'tw-bg-ink/[0.06]',
              'dark:tw-bg-white/[0.08]',
              'tw-text-ink',
              'tw-border-line/80',
              'tw-bg-accent/[0.08]',
              'tw-text-accent',
              'tw-border-accent/40'
            );
            b.classList.add(
              'tw-bg-transparent',
              'tw-text-secondary',
              'tw-border-transparent'
            );
          });
          btn.classList.add(
            'is-active',
            'tw-bg-ink/[0.06]',
            'dark:tw-bg-white/[0.08]',
            'tw-text-ink',
            'tw-border-line/80'
          );
          btn.classList.remove(
            'tw-bg-transparent',
            'tw-text-secondary',
            'tw-border-transparent'
          );

          panels.forEach((p) => p.classList.add('tw-hidden'));
          const activePanel = document.getElementById(targetId);
          if (activePanel) {
            activePanel.classList.remove('tw-hidden');
          }

          if (window.innerWidth < 768 && sidebar && contentPane) {
            sidebar.classList.add('max-md:tw--translate-x-full');
            contentPane.classList.remove('max-md:tw-translate-x-full');
          }

          this.updateMobileHeight();
        });
      });

      sheet.querySelectorAll('.bfwt-reviews-back-btn').forEach((backBtn) => {
        backBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (sidebar && contentPane) {
            sidebar.classList.remove('max-md:tw--translate-x-full');
            contentPane.classList.add('max-md:tw-translate-x-full');
          }
          this.updateMobileHeight();
        });
      });
    }

    toggle(sheetId, trigger) {
      const sheet = document.getElementById(sheetId);
      if (!sheet) {
        return;
      }

      // Check if THIS sheet is currently active (using class check for robustness)
      if (sheet.classList.contains('is-active')) {
        this.closeAll();
      } else {
        this.open(sheet, trigger);
      }
    }

    open(sheet, trigger) {
      // Close all other sheets first (but keep overlay active for smooth transition)
      this.closeAll(true);

      this.activeSheet = sheet;
      this._lastSheetTrigger = trigger || this._lastSheetTrigger;

      // Prevent outside click handler from immediately closing sheet on the current click event
      this._openedThisTick = true;
      setTimeout(() => {
        this._openedThisTick = false;
      }, 0);

      // GA / dataLayer: sheet_open or search_open
      if (typeof window.bfwt_trackEvent === 'function') {
        if (sheet.id === 'searchSheet') {
          const source =
            trigger && trigger.classList.contains('bfwt-js-search-trigger')
              ? 'hero'
              : 'header';
          window.bfwt_trackEvent('search_open', {
            source: source,
            event_category: 'search',
          });
        } else if (sheet.id !== 'sheet-newsletter') {
          window.bfwt_trackEvent('sheet_open', {
            sheet_id: sheet.id,
            event_category: 'navigation',
          });
        }
      }

      // [FIX] iOS Critical: Temporarily disable transitions and force visibility.
      sheet.style.transition = 'none';
      sheet.style.opacity = '1';
      sheet.style.visibility = 'visible';

      sheet.classList.add('is-active');
      sheet.setAttribute('aria-hidden', 'false'); // Show new sheet
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      if (this.overlay) {
        this.overlay.classList.add('is-active');
      }

      document.body.classList.add('bfwt-sheet-open');

      // Update mobile height
      setTimeout(() => {
        this.updateMobileHeight();
      }, 100);

      // Auto-focus the first input inside the sheet (search, newsletter, etc.)
      let focusTarget = sheet.querySelector(
        'input[type="email"], input[type="search"], input[type="text"]:not([readonly])'
      );

      // [Specific] Prioritize newsletter email field
      if (sheet.id === 'sheet-newsletter') {
        const mailInput = sheet.querySelector('#bfwt-sheet-email');
        if (mailInput) {
          focusTarget = mailInput;
        }
      }

      if (focusTarget) {
        // Focus Operation (Synchronous)
        focusTarget.focus({ preventScroll: true });

        // iOS Selection Range Hack
        if (focusTarget.setSelectionRange && focusTarget.type !== 'email') {
          try {
            const len = focusTarget.value.length;
            focusTarget.setSelectionRange(len, len);
          } catch (e) {
            /* ignore */
          }
        }

        // [Backup] Ensure focus sticks after any potential layout shifts/scripts
        setTimeout(() => {
          focusTarget.focus({ preventScroll: true });
        }, 100);
      }

      // Always restore transitions later
      // This is CRITICAL: If we don't clear these inline styles, sheets without inputs (Explore, Daily Drop)
      // will stay permanently visible regardless of class removal.
      setTimeout(() => {
        sheet.style.transition = '';
        sheet.style.opacity = '';
        sheet.style.visibility = '';
      }, 50);

      // Lazy Load Turnstile (Newsletter Only)
      if (sheet.id === 'sheet-newsletter' && window.loadTurnstile) {
        window.loadTurnstile();
      }


    }

    closeAll(keepOverlay = false) {
      // Find ANY sheet with stuck inline visibility, just in case
      // The selector finds any sheet that might have inline styles set
      const allSheets = document.querySelectorAll('.bfwt-floating-sheet');

      allSheets.forEach((sheet) => {
        // Prevent ARIA warning: Blur focus if inside the sheet being hidden
        if (sheet.contains(document.activeElement)) {
          document.activeElement.blur();
        }

        // Clear state
        sheet.classList.remove('is-active');
        sheet.setAttribute('aria-hidden', 'true'); // Hide sheet

        // Clear stuck inline styles (critical for fix)
        sheet.style.opacity = '';
        sheet.style.visibility = '';
        sheet.style.transition = '';

        // Reset trigger aria state
        if (sheet.id) {
          const trigger = document.querySelector(
            `[data-sheet-target="${sheet.id}"]`
          );
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
          }
        }
      });

      this.activeSheet = null;

      // Only close overlay/body if explicitly requested (default)
      // When switching sheets (open calling closeAll), keepOverlay is true.
      if (!keepOverlay) {
        if (this.overlay) {
          this.overlay.classList.remove('is-active');
        }
        document.body.classList.remove('bfwt-sheet-open');

        // Return focus to the trigger that opened the sheet (accessibility)
        if (
          this._lastSheetTrigger &&
          typeof this._lastSheetTrigger.focus === 'function'
        ) {
          try {
            this._lastSheetTrigger.focus();
          } catch (_) {}
        }
        this._lastSheetTrigger = null;
      }
    }

    updateMobileHeight() {
      const sheet = this.activeSheet;
      if (!sheet) return;

      if (window.innerWidth >= 768) {
        sheet.style.height = '';
        return;
      }

      const getViewportMaxHeight = (targetSheet) => {
        const vh = window.visualViewport
          ? window.visualViewport.height
          : window.innerHeight;
        const rect = targetSheet ? targetSheet.getBoundingClientRect() : null;
        const top = rect && rect.top > 0 ? rect.top : 56;
        return Math.max(280, vh - top - 16);
      };

      const maxAllowedHeight = getViewportMaxHeight(sheet);

      // 1. searchSheet height logic delegator
      if (sheet.id === 'searchSheet') {
        if (typeof window.bfwtUpdateMobileHeight === 'function') {
          window.bfwtUpdateMobileHeight();
        }
        return;
      }

      // 2. accountSheet height logic
      if (sheet.id === 'accountSheet') {
        const loggedOutView = document.getElementById(
          'bfwt-account-logged-out-view'
        );
        const loggedInView = document.getElementById(
          'bfwt-account-logged-in-view'
        );

        if (loggedOutView && !loggedOutView.classList.contains('tw-hidden')) {
          const header = loggedOutView.querySelector('div:first-child');
          const formsContainer = loggedOutView.querySelector('.tw-flex-1');

          const headerHeight = header ? header.offsetHeight : 48;
          const visibleForm = formsContainer
            ? formsContainer.querySelector(
                'form:not(.tw-hidden), div:not(.tw-hidden)'
              )
            : null;
          const contentHeight = visibleForm
            ? visibleForm.scrollHeight + 64
            : 400;

          const targetHeight = Math.min(
            headerHeight + contentHeight,
            maxAllowedHeight
          );
          sheet.style.height = targetHeight + 'px';
          return;
        }

        if (loggedInView && !loggedInView.classList.contains('tw-hidden')) {
          const header = loggedInView.querySelector('div:first-child');
          const sidebar = loggedInView.querySelector(
            '.bfwt-account-sidebar-el'
          );
          const contentPane = loggedInView.querySelector(
            '.bfwt-account-content-pane-el'
          );
          const panels = loggedInView.querySelectorAll('.bfwt-account-panel');

          // Footers
          const appPromo = loggedInView.querySelector(
            'div.tw-flex-none.tw-flex.tw-items-center.tw-justify-between'
          );
          const logoutBtnRow = loggedInView.querySelector('div.md\\:tw-hidden');

          const headerHeight = header ? header.offsetHeight : 52;
          const appPromoHeight =
            appPromo && appPromo.offsetHeight ? appPromo.offsetHeight : 0;
          const logoutBtnHeight =
            logoutBtnRow && logoutBtnRow.offsetHeight
              ? logoutBtnRow.offsetHeight
              : 0;
          const footersHeight = appPromoHeight + logoutBtnHeight;

          const isSidebarActive =
            sidebar &&
            !sidebar.classList.contains('max-md:tw--translate-x-full');
          let contentHeight = 350;

          if (isSidebarActive) {
            const sidebarInner = sidebar.querySelector('div');
            contentHeight = sidebarInner ? sidebarInner.offsetHeight : 350;
          } else if (contentPane) {
            const activePanel = Array.from(panels).find(
              (p) => p && !p.classList.contains('tw-hidden')
            );
            if (activePanel) {
              contentHeight = activePanel.scrollHeight + 32;
            }
          }

          const targetHeight = Math.min(
            headerHeight + contentHeight + footersHeight,
            maxAllowedHeight
          );
          sheet.style.height = targetHeight + 'px';
          return;
        }
      }

      // 3. sheet-saved height logic
      if (sheet.id === 'sheet-saved') {
        const guestView = document.getElementById('bf-saved-guest-view');
        const memberView = document.getElementById('bf-saved-member-view');
        const header = sheet.querySelector('div:first-child');
        const headerHeight = header ? header.offsetHeight : 52;
        const appPromo = sheet.querySelector('div.tw-flex-none.tw-flex.tw-items-center.tw-justify-between');
        const appPromoHeight = appPromo && appPromo.offsetHeight ? appPromo.offsetHeight : 0;

        if (guestView && !guestView.classList.contains('tw-hidden')) {
          const contentHeight = guestView.scrollHeight + 32;
          sheet.style.height = Math.min(headerHeight + contentHeight + appPromoHeight, maxAllowedHeight) + 'px';
          return;
        }
        if (memberView && !memberView.classList.contains('tw-hidden')) {
          const sidebar = memberView.querySelector('.bfwt-saved-sidebar-el');
          const contentPane = memberView.querySelector('.bfwt-saved-content-pane-el');
          const panels = memberView.querySelectorAll('.bf-saved-panel');
          const isSidebarActive = sidebar && !sidebar.classList.contains('max-md:tw--translate-x-full');
          let contentHeight = 350;
          if (window.innerWidth < 768 && isSidebarActive) {
            const sidebarInner = sidebar.querySelector('div');
            contentHeight = sidebarInner ? sidebarInner.offsetHeight : 350;
          } else if (contentPane) {
            const activePanel = Array.from(panels).find((p) => p && !p.classList.contains('tw-hidden'));
            if (activePanel) contentHeight = activePanel.scrollHeight + 32;
          }
          sheet.style.height = Math.min(headerHeight + contentHeight + appPromoHeight, maxAllowedHeight) + 'px';
          return;
        }
      }

      // 4. sheet-reviews height logic
      if (sheet.id === 'sheet-reviews') {
        const header = sheet.querySelector('div:first-child');
        const headerHeight = header ? header.offsetHeight : 52;
        const appPromo = sheet.querySelector(
          'div.tw-flex-none.tw-flex.tw-items-center.tw-justify-between'
        );
        const appPromoHeight =
          appPromo && appPromo.offsetHeight ? appPromo.offsetHeight : 0;

        const sidebar = sheet.querySelector('.bfwt-reviews-sidebar-el');
        const contentPane = sheet.querySelector('.bfwt-reviews-content-pane-el');
        const panels = sheet.querySelectorAll('.bfwt-reviews-panel');

        const isSidebarActive =
          sidebar && !sidebar.classList.contains('max-md:tw--translate-x-full');
        let contentHeight = 350;

        if (window.innerWidth < 768 && isSidebarActive) {
          const sidebarInner = sidebar.querySelector('div');
          contentHeight = sidebarInner ? sidebarInner.offsetHeight : 350;
        } else if (contentPane) {
          const activePanel = Array.from(panels).find(
            (p) => p && !p.classList.contains('tw-hidden')
          );
          if (activePanel) {
            contentHeight = activePanel.scrollHeight + 32;
          }
        }

        const targetHeight = Math.min(
          headerHeight + contentHeight + appPromoHeight,
          maxAllowedHeight
        );
        sheet.style.height = targetHeight + 'px';
        return;
      }

      // 5. Default / Fallback height calculation
      sheet.style.height =
        Math.min(sheet.scrollHeight, maxAllowedHeight) + 'px';
    }
  }

  window.sheetManager = new SheetManager();
});
